import { createContext, useContext, ReactNode, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { AuthService } from '@/services/core/auth'
import { UserApi, ApiUser } from '@/services/users/userApi'
import { setLocale } from '../utils/locale'

const UserContext = createContext<ApiUser | null>(null)
const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

export function UserProvider({ children }: { children: ReactNode }) {
  const authenticated = AuthService.isAuthenticated()
  const identifiedUserId = useRef<string | null>(null)

  const { data: user = null } = useQuery({
    queryKey: ['currentUser'],
    queryFn: UserApi.getCurrent,
    enabled: authenticated,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (!posthogConfigured) return
    if (!user?.posthogDistinctId) {
      identifiedUserId.current = null
      return
    }
    if (identifiedUserId.current === user.posthogDistinctId) return

    if (identifiedUserId.current) posthog.reset()

    posthog.identify(user.posthogDistinctId, {
      ...(user.username ? { email: user.username } : {}),
      ...(user.displayName || user.name ? { name: user.displayName || user.name } : {}),
    })
    identifiedUserId.current = user.posthogDistinctId
  }, [user])

  // Every number and date formatter reads this, and setting it here — above
  // the whole tree — means a user arriving or changing their preference
  // re-renders everything that shows one.
  setLocale(user?.locale)

  return (
    <UserContext.Provider value={user}>
      {children}
    </UserContext.Provider>
  )
}

export function useCurrentUser() {
  return useContext(UserContext)
}
